<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import type { Opportunity, UpdateOpportunityDto } from '@/api-swagger'
import { formatDate, removeTime } from '@/utils/dateUtils'
import Modal from '@/components/common/layout/Modal.vue'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/useUserStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { usePartnerStore } from '@/stores/usePartnerStore'
import Datepicker from '@vuepic/vue-datepicker'
import MonetaryInput from '@/components/common/inputs/MonetaryInput.vue'
import RadioInput from '@/components/common/inputs/RadioInput.vue'
import SelectSimple from '@/components/common/selects/SelectSimple.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'
import type { Context } from '@/utils/types'
import { useSaleOrdersStore } from '@/stores/useSaleOrderStore'
import SelectSaleOrderOpportunity from '@/components/common/selects/SelectSaleOrderOpportunity.vue'
import { UserRole } from '@/utils/enums'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { VQuillEditor } from '@morpheme/quill-editor'
import { object, string } from 'yup'
import { useForm } from 'vee-validate'
import { useAttachmentStore } from '@/stores/useAttachmentStore'
import PreviewFiles from '@/components/common/ui/PreviewFiles.vue'
import OpportunityPartnerInfo from '@/components/features/opportunities/OpportunityPartnerInfo.vue'
import FileInput from '@/components/common/inputs/FileInput.vue'
import type { FileInputExpose } from '@/types/fileInput'

const emit = defineEmits(['close'])
const props = defineProps({
  opportunity: {
    type: Object as () => Opportunity,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
    required: true,
  },
})

const opportunityStore = useOpportunityStore()
const saleOrderStore = useSaleOrdersStore()
const attachmentStore = useAttachmentStore()
const { saleOrders } = storeToRefs(saleOrderStore)
const { stages, recurringPlans, loading } = storeToRefs(opportunityStore)
const partnerStore = usePartnerStore()
const { partner } = storeToRefs(partnerStore)
const userStore = useUserStore()
const myTeamStore = useMyTeamStore()
const boostCoreStore = useBoostCorePermissionStore()
const { userConnected, users, role } = storeToRefs(userStore)

const commercialOptions = computed(() =>
  myTeamStore.all
    .map((m) => {
      const id = m.metadata?.odoo16Id
      if (typeof id !== 'number') return null
      return {
        id,
        text: myTeamStore.memberLabel(m),
        subText: m.email || '',
      }
    })
    .filter((o): o is { id: number; text: string; subText: string } => o !== null),
)

const $toast = useToast()

const editableOpportunity = ref<Opportunity>({ ...props.opportunity })
const selectionsStore = useSelectionsStore()
const { salesFamilyOpportunitySelections, typeOfSaleOpportunitySelections } =
  storeToRefs(selectionsStore)
const attachmentRef = ref<FileInputExpose | null>(null)

const salesFamilyOptions = computed(() =>
  salesFamilyOpportunitySelections.value.map((item) => ({
    id: item.value,
    value: item.value,
    label: item.name,
  })),
)

const typeOfSaleOptions = computed(() =>
  typeOfSaleOpportunitySelections.value.map((item) => ({
    id: item.value,
    value: item.value,
    label: item.name,
  })),
)
const context = inject<Context>('context', '')

const schema = object({
  content: string().required().label('Content'),
})

const { values } = useForm({
  validationSchema: schema,
})

onMounted(() => {
  if (userConnected.value === null) {
    userStore.getUserByEmail(boostCoreStore.user?.email)
  }
  opportunityStore.getAllRecurringPlans()
})

const recurringPlanOptions = computed(() =>
  (recurringPlans.value || []).map((p) => ({
    id: p.id,
    value: p.id,
    label: p.name,
  })),
)

watch(
  () => props.opportunity,
  (next) => {
    if (next) editableOpportunity.value = { ...next }
  },
  { deep: true },
)

watch(
  () => editableOpportunity.value?.userId,
  async () => {
    const userId = editableOpportunity.value?.userId
    const partner = editableOpportunity.value?.partnerId
    await attachmentStore.getAllAttachmentByRessourceId(props.opportunity.id)
    if (userId && partner) {
      saleOrderStore.getSaleOrders(userId, partner.id)
    }
  },
  { immediate: true },
)

const errors = ref<Record<string, string>>({})

const requiredFields = [
  { field: 'name', label: "objet de l'opportunité" },
  { field: 'salesFamily', label: 'portefeuille' },
  { field: 'userId', label: 'commercial' },
  {  field: 'dateDeadline', label: 'date de signature prévue'}
]

const hasRecurringRevenue = computed(
  () => (editableOpportunity.value?.recurringRevenue ?? 0) > 0,
)

const onSubmit = async () => {
  if (
    editableOpportunity.value.expectedRevenue < 0 ||
    editableOpportunity.value.recurringRevenue < 0
  ) {
    $toast.warning('Le montant estimé ne peut pas être négatif.')
    return
  }

  const updatedOpportunity: UpdateOpportunityDto = {
    name: editableOpportunity.value.name,
    userId: editableOpportunity.value.userId,
    priority: editableOpportunity.value.priority.toString(),
    salesEngineer: editableOpportunity.value.salesEngineer,
    salesFamily: editableOpportunity.value.salesFamily,
    typeOfSale: editableOpportunity.value.typeOfSale,
    expectedRevenue: editableOpportunity.value.expectedRevenue,
    recurringRevenue: editableOpportunity.value.recurringRevenue,
    recurringPlanId: hasRecurringRevenue.value
      ? (editableOpportunity.value.recurringPlanId ?? null)
      : null,
    dateDeadline: removeTime(
      new Date(editableOpportunity.value.dateDeadline || props.opportunity.dateDeadline),
    ),
    stageId: editableOpportunity.value.stageId,
    orderIds: editableOpportunity.value.orderIds,
    description: values.description,
  }
  errors.value = {}
  requiredFields.forEach(({ field, label }) => {
    if (!updatedOpportunity[field as keyof typeof updatedOpportunity]) {
      errors.value[field] = `Le champ ${label} est requis.`
    }
  })
  if (hasRecurringRevenue.value && !updatedOpportunity.recurringPlanId) {
    errors.value.recurringPlanId = 'La périodicité est requise pour une marge récurrente.'
  }

  if (Object.keys(errors.value).length > 0) {
    return
  }

  await opportunityStore
    .updateOpportunity(props.opportunity.id, updatedOpportunity, context)
    .then(() => {
      if (partner.value) {
        partnerStore.searchId(partner.value.id)
      }

      $toast.success('Affaire mise à jour avec succès.')
      if (attachmentRef.value) {
        const files = attachmentRef.value?.fileInputRef?.files

        if (files && files.length > 0) {
          const fileArray: File[] = Array.from(files)

          attachmentStore
            .uploadAttachments(fileArray, 'crm.lead', props.opportunity.id || 0)
            .then(() => {
              $toast.success('Pièces jointes enregistrées avec succès.')
            })
            .catch((error: { message: string }) => {
              $toast.error(`Erreur lors de l'enregistrement des pièces jointes : ${error.message}`)
            })
        }
      }
      emit('close')
    })
    .catch((error) => {
      $toast.error(error.message)
    })
}
</script>

<template>
  <Modal
    :showModal="showModal"
    @close="emit('close')"
    title="Détails de l'affaire"
    :loading="loading"
  >
    <template #content>
      <p class="text-end">Créée le {{ formatDate(editableOpportunity.createDate) }}</p>
      <form @submit.prevent="onSubmit" class="space-y-4">
        <div :class="role === UserRole.Manager ? 'grid grid-cols-2 gap-4' : ''">
          <TextInput
            v-model="editableOpportunity!.name"
            label="Objet de l'affaire"
            required
            :errorMessage="errors.name"
          />
          <div>
            <SelectWithAvatar
              v-if="role === UserRole.Manager && userConnected"
              label="Commercial"
              v-model="editableOpportunity!.userId"
              :options="commercialOptions"
            />
          </div>
        </div>
        <div class="mt-3">
          <OpportunityPartnerInfo :partner="editableOpportunity.partnerId"></OpportunityPartnerInfo>
        </div>

        <div class="grid lg:grid-cols-2 gap-4">
          <SelectWithAvatar
            label="Avant-Vente"
            v-model="editableOpportunity.salesEngineer"
            :options="users?.map((u) => ({ id: u.id, text: u.name, subText: u.email || '' })) || []"
          />
        </div>
        <div class="grid lg:grid-cols-2 2xl:grid-cols-4 gap-4">
          <div>
            <RadioInput
              label="Forecast ⭐⭐⭐"
              :options="[
                { id: '3', value: '3', label: 'Oui' },
                { id: '1', value: '1', label: 'Non' },
              ]"
              v-model="editableOpportunity!.priority"
            />
          </div>
          <div>
            <RadioInput
              label="Type de vente"
              :options="typeOfSaleOptions"
              v-model="editableOpportunity!.typeOfSale"
            />
          </div>

          <div>
            <SelectSimple
              label="Type de portefeuille"
              :options="salesFamilyOptions"
              v-model="editableOpportunity!.salesFamily"
              required
              :errorMessage="errors.salesFamily"
            />
          </div>

          <div>
            <SelectSimple
              label="Etape"
              :options="
                stages?.map((stage) => ({
                  id: stage.id,
                  value: stage.id,
                  label: stage.name || '',
                })) || []
              "
              v-model="editableOpportunity!.stageId"
            />
          </div>
        </div>
        <div class="grid lg:grid-cols-3 gap-4">
          <div>
            <label class="label" for="opportunity-deadline">
              <span class="label-text font-semibold">Date de signature prévue *</span>
            </label>
            <Datepicker
              v-model="editableOpportunity!.dateDeadline"
              id="opportunity-deadline"
              locale="fr"
              :enable-time-picker="false"
              auto-apply
              text-input
              format="dd/MM/yyyy"
              :disabled-week-days="[6, 0]"
              required
            >
            </Datepicker>
          </div>

          <div>
            <MonetaryInput label="Marge One Shot" v-model="editableOpportunity!.expectedRevenue" />
          </div>

          <div>
            <MonetaryInput
              label="Marge récurrente"
              v-model="editableOpportunity!.recurringRevenue"
            />
          </div>
        </div>
        <div v-if="hasRecurringRevenue" class="grid lg:grid-cols-3 gap-4">
          <div>
            <SelectSimple
              label="Périodicité"
              :options="recurringPlanOptions"
              v-model="editableOpportunity!.recurringPlanId"
              required
              :errorMessage="errors.recurringPlanId"
            />
          </div>
        </div>
        <div class="grid lg:grid-cols-2 gap-4" v-if="saleOrders && saleOrders.length > 0">
          <select-sale-order-opportunity
            label="Devis liés"
            :options="
              saleOrders.map((o) => ({
                id: o.id,
                text: o.name,
                subText: o.partnerId.name || '',
              })) || []
            "
            v-model="editableOpportunity!.orderIds"
            :multiple="true"
          ></select-sale-order-opportunity>
        </div>
        <div v-else-if="!saleOrders" class="skeleton h-12 w-1/2"></div>
        <div v-if="saleOrders && saleOrders.length == 0">
          <span class="label-text font-semibold m-2"> Devis liés </span>
          <div class="input input-disabled w-1/2 m-2">Aucun devis à lier</div>
        </div>
        <div>
          <label class="label" for="description">
            <span class="label-text font-semibold"> Description </span>
          </label>
          <VQuillEditor
            name="description"
            v-model="editableOpportunity!.description"
            placeholder="Description"
          />
        </div>
        <div class="flex justify-between gap-4">
          <FileInput ref="attachmentRef" label="Pièces jointes" :multiple="true" />

          <SaveButton :loading="loading" />
        </div>
      </form>
      <PreviewFiles :ressourceId="editableOpportunity.id" />
    </template>
  </Modal>
</template>
